import { useState, useRef, useEffect } from 'react';
import { ChevronDown, ArrowUpDown } from 'lucide-react';
import type { FavoriteSortOption } from '@/types/favorites';

interface FavoritesSortDropdownProps {
  value: FavoriteSortOption;
  onChange: (value: FavoriteSortOption) => void;
}

interface SortOptionConfig {
  value: FavoriteSortOption;
  label: string;
}

const SORT_OPTIONS: SortOptionConfig[] = [
  { value: 'date_added_desc', label: 'Date Added (Newest)' },
  { value: 'date_added_asc', label: 'Date Added (Oldest)' },
  { value: 'item_date_desc', label: 'Item Date (Newest)' },
  { value: 'item_date_asc', label: 'Item Date (Oldest)' },
  { value: 'alpha_asc', label: 'Alphabetical (A-Z)' },
  { value: 'alpha_desc', label: 'Alphabetical (Z-A)' },
];

export function FavoritesSortDropdown({ value, onChange }: FavoritesSortDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentOption = SORT_OPTIONS.find((opt) => opt.value === value) || SORT_OPTIONS[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (option: FavoriteSortOption) => {
    onChange(option);
    setIsOpen(false);
  };

  return (
    <div className="px-4 py-2 border-b border-slate-100 bg-white" ref={dropdownRef}>
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
        >
          <ArrowUpDown size={14} className="text-slate-400" />
          <span>Sort: {currentOption.label}</span>
          <ChevronDown
            size={14}
            className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20 min-w-[200px]">
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={`
                  w-full text-left px-4 py-2 text-sm transition-colors
                  ${
                    option.value === value
                      ? 'bg-[#f8e8f8] text-[#8e4585] font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }
                `}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
